/**
 * SignatureElectroniqueModal — Flow OTP en 2 étapes pour signer
 * électroniquement un Devis ESS DOS-DOMIUM.
 *
 * Étape 1 : demande d'OTP (email envoyé via Brevo backend)
 * Étape 2 : saisie du code 6 chiffres + nom du signataire
 *
 * Une fois signé, le devis bascule automatiquement sur le titre
 * "APPEL À CONTRIBUTION COOPÉRATIVE — DOCUMENT VALANT FACTURE"
 * (logique pilotée 100% côté backend, le composant déclenche juste un
 * refresh / nouveau download du PDF).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Loader2, ShieldCheck, Mail, AlertCircle, CheckCircle2, Pen } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignatureElectroniqueModal({
  open,
  onClose,
  reference,
  signataireDefaultNom = '',
  onSigned, // callback (signedAt) → permet au parent de refresh le devis
}) {
  const [step, setStep] = useState('idle'); // idle | requesting | awaiting | verifying | signed
  const [otp, setOtp] = useState('');
  const [signataireNom, setSignataireNom] = useState(signataireDefaultNom);
  const [maskedEmail, setMaskedEmail] = useState('');
  const [expiresAt, setExpiresAt] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [signedInfo, setSignedInfo] = useState(null);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!open) {
      // Reset à la fermeture
      setStep('idle'); setOtp(''); setMaskedEmail(''); setExpiresAt(null);
      setErrorMsg(''); setSignedInfo(null); setSecondsLeft(0);
      setSignataireNom(signataireDefaultNom);
    }
  }, [open, signataireDefaultNom]);

  // Compteur de secondes restantes
  useEffect(() => {
    if (step !== 'awaiting' || !expiresAt) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left === 0) {
        setErrorMsg('Le code a expiré. Demandez un nouveau code.');
      }
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [step, expiresAt]);

  const getToken = () => localStorage.getItem('token') || sessionStorage.getItem('token');

  const headers = () => ({ Authorization: `Bearer ${getToken()}` });

  const requestOtp = async () => {
    setErrorMsg('');
    setStep('requesting');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(reference)}/request-signature`,
        {},
        { headers: headers() },
      );
      setMaskedEmail(res.data.masked_email);
      setExpiresAt(res.data.expires_at);
      setStep('awaiting');
      toast.success(`Code envoyé à ${res.data.masked_email}`);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Échec de l\'envoi du code';
      setErrorMsg(msg);
      setStep('idle');
      toast.error(msg);
    }
  };

  const verifyOtp = async () => {
    if (otp.trim().length !== 6) {
      setErrorMsg('Le code doit contenir 6 chiffres.');
      return;
    }
    setErrorMsg('');
    setStep('verifying');
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/domium/devis/${encodeURIComponent(reference)}/verify-signature`,
        { otp: otp.trim(), signataire_nom: signataireNom.trim() || undefined },
        { headers: headers() },
      );
      setSignedInfo({ signed_at: res.data.signed_at });
      setStep('signed');
      toast.success('Devis signé électroniquement avec succès !');
      if (onSigned) onSigned(res.data.signed_at);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Code invalide';
      setErrorMsg(msg);
      setStep('awaiting');
    }
  };

  const renderIdle = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <ShieldCheck className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-900 leading-relaxed">
          Vous allez signer électroniquement votre <strong>Devis ESS DOS-DOMIUM</strong>.
          Un code de validation à usage unique sera envoyé à l'adresse email du signataire enregistrée dans la souscription. Le code est valable <strong>10 minutes</strong>.
        </div>
      </div>
      <div className="text-xs text-slate-500 leading-relaxed">
        En cliquant sur « Recevoir le code », vous initiez le processus de signature électronique. La signature équivaut à apposer la mention manuscrite « Lu et approuvé — Bon pour accord et engagement coopératif » et à valider votre adhésion au dispositif coopératif.
      </div>
      <Button
        onClick={requestOtp}
        disabled={step === 'requesting'}
        className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
        data-testid="btn-request-otp"
      >
        {step === 'requesting' ? (
          <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Envoi en cours...</>
        ) : (
          <><Mail className="h-4 w-4 mr-2" /> Recevoir le code par email</>
        )}
      </Button>
    </div>
  );

  const renderAwaiting = () => (
    <div className="space-y-4">
      <div className="flex items-start gap-3 p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
        <Mail className="h-5 w-5 text-emerald-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-emerald-900">
          Code envoyé à <strong>{maskedEmail}</strong>. Vérifiez aussi vos spams.
        </div>
      </div>
      {secondsLeft > 0 && (
        <div className="text-center text-xs text-slate-500" data-testid="otp-countdown">
          Code valable encore <strong>{Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}</strong>
        </div>
      )}
      <div>
        <Label htmlFor="otp-input" className="text-sm font-medium">Code à 6 chiffres</Label>
        <Input
          id="otp-input"
          type="text"
          inputMode="numeric"
          maxLength={6}
          autoComplete="one-time-code"
          value={otp}
          onChange={(e) => setOtp(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
          placeholder="000000"
          className="text-center text-2xl font-bold tracking-[0.5em] font-mono mt-1.5"
          data-testid="input-otp"
        />
      </div>
      <div>
        <Label htmlFor="signataire-nom" className="text-sm font-medium">Nom du signataire</Label>
        <Input
          id="signataire-nom"
          value={signataireNom}
          onChange={(e) => setSignataireNom(e.target.value)}
          placeholder="Nom & prénom du signataire"
          className="mt-1.5"
          data-testid="input-signataire-nom"
        />
      </div>
      {errorMsg && (
        <div className="flex items-start gap-2 p-2 bg-red-50 border border-red-200 rounded text-sm text-red-700">
          <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
          {errorMsg}
        </div>
      )}
      <div className="flex gap-2">
        <Button variant="outline" onClick={requestOtp} disabled={step === 'verifying'} className="flex-1" data-testid="btn-resend-otp">
          Renvoyer
        </Button>
        <Button
          onClick={verifyOtp}
          disabled={step === 'verifying' || otp.length !== 6}
          className="flex-1 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700"
          data-testid="btn-verify-otp"
        >
          {step === 'verifying' ? (
            <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Vérification...</>
          ) : (
            <><Pen className="h-4 w-4 mr-2" /> Signer le devis</>
          )}
        </Button>
      </div>
    </div>
  );

  const renderSigned = () => (
    <div className="space-y-4 text-center" data-testid="signature-success">
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center">
          <CheckCircle2 className="h-9 w-9 text-emerald-600" />
        </div>
      </div>
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Devis signé !</h3>
        <p className="text-sm text-slate-600 mt-1">
          Votre engagement coopératif a été enregistré. Le devis bascule automatiquement en
          <strong> « Document valant facture »</strong>.
        </p>
        {signedInfo?.signed_at && (
          <p className="text-xs text-slate-500 mt-2">
            Signé le {new Date(signedInfo.signed_at).toLocaleString('fr-FR')}
          </p>
        )}
      </div>
      <Button onClick={onClose} className="w-full" data-testid="btn-close-success">
        Fermer
      </Button>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="signature-electronique-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-blue-600" />
            Signature électronique du devis
          </DialogTitle>
          <DialogDescription>
            Référence : <code className="text-xs bg-slate-100 px-1.5 py-0.5 rounded">{reference}</code>
          </DialogDescription>
        </DialogHeader>
        {step === 'idle' || step === 'requesting' ? renderIdle() : null}
        {(step === 'awaiting' || step === 'verifying') ? renderAwaiting() : null}
        {step === 'signed' ? renderSigned() : null}
      </DialogContent>
    </Dialog>
  );
}
